import React, { useState } from 'react';
import { Clock, Filter, Users, MapPin } from 'lucide-react';
import { dojoData } from '../mock';

const Schedule = () => {
  const [selectedLevel, setSelectedLevel] = useState('Tous');
  
  const levels = ['Tous', 'Enfant', 'Débutant', 'Confirmé', 'Tous niveaux'];

  const handleContactRedirect = () => {
    const contactFormElement = document.getElementById('contact-form');
    if (contactFormElement) {
      const headerHeight = 100; // Account for header height
      const elementPosition = contactFormElement.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };
  
  const getLocationById = (locationId) => {
    return dojoData.locations.find(loc => loc.id === locationId);
  };

  const getProfessorAnchor = (instructorName) => {
    return instructorName.toLowerCase().replace(/\s+/g, '-').replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a').replace(/[ôö]/g, 'o').replace(/[ùûü]/g, 'u').replace(/[îï]/g, 'i').replace(/ç/g, 'c').replace(/[^a-z0-9-]/g, '');
  };

  const filteredSchedule = dojoData.schedule.map(day => ({
    ...day,
    classes: selectedLevel === 'Tous' 
      ? day.classes 
      : selectedLevel === 'Tous niveaux'
        ? day.classes.filter(cls => cls.level !== 'Enfant')
        : day.classes.filter(cls => 
            cls.level.includes(selectedLevel) || (cls.level === 'Tous niveaux' && selectedLevel !== 'Enfant')
          )
  })).filter(day => day.classes.length > 0);

  return (
    <section id="schedule" className="section-padding section-light">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-6" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Horaires</h2>
          <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-on-light)' }}>
            Trouvez la session qui vous correspond le mieux. Tous les niveaux sont bienvenus.
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <Filter size={20} style={{ color: 'var(--text-secondary-light)' }} />
            <span className="body-small" style={{ color: 'var(--text-secondary-light)' }}>Filtrer par catégorie :</span>
          </div>
          {levels.map(level => (
            <button
              key={level}
              onClick={() => setSelectedLevel(level)}
              className={`px-4 py-2 rounded border transition-all ${
                selectedLevel === level 
                  ? 'btn-primary' 
                  : 'border-opacity-50 hover:border-opacity-100'
              }`}
              style={{
                ...(selectedLevel !== level && {
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary-light)',
                  borderColor: 'var(--border-medium)'
                })
              }}
            >
              {level}
            </button>
          ))}
        </div>

        {/* Schedule Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredSchedule.map((day, dayIndex) => (
            <div key={dayIndex} className="border rounded-lg overflow-hidden card-light"
                 style={{ borderColor: 'var(--border-medium)' }}>
              
              {/* Day Header */}
              <div className="p-6 border-b"
                   style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--border-light)' }}>
                <h3 className="heading-3 text-center" style={{ color: 'white', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                  {day.day}
                </h3>
              </div>

              {/* Classes */}
              <div className="p-4 space-y-4">
                {day.classes.length > 0 ? (
                  day.classes.map((classItem, classIndex) => (
                    <div key={classIndex} 
                         className="p-4 rounded border transition-all hover:bg-opacity-50"
                         style={{ 
                           backgroundColor: 'var(--bg-card-light)', 
                           borderColor: 'var(--border-light)' 
                         }}>
                      
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold" style={{ color: 'var(--brand-primary)' }}>
                          {classItem.name}
                        </h4>
                        <span className="text-sm px-2 py-1 rounded"
                              style={{ 
                                backgroundColor: 'var(--brand-primary)', 
                                color: 'white',
                                fontSize: '0.8rem'
                              }}>
                          {classItem.level}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        <div className="flex items-center gap-1">
                          <Clock size={16} style={{ color: 'var(--text-secondary-light)' }} />
                          <span className="body-small" style={{ color: 'var(--text-on-light)' }}>{classItem.time}</span>
                        </div>
                        {classItem.locationId && (
                          <div className="flex items-center gap-1">
                            <MapPin size={16} style={{ color: 'var(--text-secondary-light)' }} />
                            <a 
                              href={`#location?loc=${classItem.locationId}`}
                              className="body-small text-blue-600 hover:text-blue-800 underline cursor-pointer"
                              title={`Voir l'adresse: ${getLocationById(classItem.locationId)?.address}`}
                            >
                              {getLocationById(classItem.locationId)?.name.split(' - ')[1] || getLocationById(classItem.locationId)?.name}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users size={16} style={{ color: 'var(--text-secondary-light)' }} />
                          <a 
                            href={`/professeurs#professor-${getProfessorAnchor(classItem.instructor)}`}
                            className="body-small text-blue-600 hover:text-blue-800 underline cursor-pointer"
                            title={`Voir le profil de ${classItem.instructor}`}
                          >
                            {classItem.instructor}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="body-small" style={{ color: 'var(--text-secondary-light)' }}>Aucun cours ne correspond au filtre sélectionné</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="body-medium mb-6" style={{ color: 'var(--text-on-light)' }}>
            Prết à démarrer votre voyage dans les arts martiaux ?
          </p>
          <button className="btn-primary" onClick={handleContactRedirect}>
            Réservez votre séance d'essai
          </button>
        </div>
      </div>
    </section>
  );
};

export default Schedule;
